<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-input
              v-model="search"
              placeholder="請輸入公司名稱"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-button :loading="loading" class="is-primary" @click="loadItems">
              {{ $t('attributes.search') }}
            </b-button>
          </b-field>
        </b-field>
      </div>
      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="loading"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') }}
          </b-button>
        </div>
      </div>
    </nav>
    <b-table
      :data="items"
      :loading="loading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="business_number"
        :label="$t('attributes.business_number')"
        width="150"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.business_number }}</p>
      </b-table-column>
      <b-table-column
        field="name"
        :label="$t('attributes.company')"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">{{ props.row.name }}</p>
      </b-table-column>
      <b-table-column
        field="bank"
        :label="$t('attributes.bank')"
        sortable
        centered
        v-slot="props"
      >
        <p class="has-text-weight-medium">
          【{{ props.row.bank_number }}】 {{ props.row.bank_name }}
        </p>
      </b-table-column>
      <b-table-column
        :label="$t('attributes.function')"
        width="220"
        centered
        v-slot="props"
      >
        <b-button type="is-primary" @click.prevent="onModify(props.row)">
          {{ $t('attributes.modify') }}
        </b-button>
        <b-button
          type="is-danger"
          @click.prevent="onRemove(props.row)"
          style="margin-left: 20px;"
        >
          {{ $t('attributes.delete') }}
        </b-button>
      </b-table-column>
    </b-table>
    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <BusinessEntityDetail
        :mode="formMode"
        :data="selectedItem"
        @send="onSend"
        @close="isFormModalActive = false"
      />
    </b-modal>
  </section>
</template>
<script>
import { BUSINESS_ENTITY_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import BusinessEntityDetail from '@/components/basic/BusinessEntityDetail'

export default {
  name: 'BusinessEntityList',
  components: { BusinessEntityDetail },
  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      isFormModalActive: false,
      formMode: '',

      total: 0,
      sort_by: this.query.sort || 'created_at',
      by_descending: this.query.order === 'desc',
      defaultSortOrder: 'desc',
      page: parseInt(this.query.page) || 1,
      perPage: this.query.page_size || 30,
      sortField: this.query.sort_by || 'created_at',
      sortOrder: this.query.order || 'desc',

      search: '',
      items: [],
      selectedItem: null
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${BUSINESS_ENTITY_API}`
        const params = {
          search: this.search,
          sort_by: this.sort_by,
          by_descending: this.by_descending,
          page_size: this.perPage,
          page_number: this.page
        }
        this.loading = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data
        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.selectedItem = null
        this.items = []
        this.total = 0

        notifyService.fetch_error('bank', e)
      } finally {
        this.loading = false
      }
    },
    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    },
    onAdd() {
      this.selectedItem = {
        id: '',
        name: '',
        business_number: '',
        bank_name: null,
        bank_number: null,
        branch_name: '',
        account_number: '',
        account_name: ''
      }
      this.formMode = 'create'
      this.isFormModalActive = true
    },
    onSend(item) {
      if (this.formMode === 'create') {
        this.onCreated(item)
      } else {
        this.onUpdated(item)
      }
    },
    async onCreated(item) {
      try {
        this.sending = true
        await this.axios.post(BUSINESS_ENTITY_API, item)
        this.selectedItem = {
          id: '',
          name: '',
          business_number: '',
          bank_name: null,
          bank_number: null,
          branch_name: '',
          account_number: '',
          account_name: ''
        }
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))
        await this.loadItems()
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async onUpdated(item) {
      try {
        this.sending = true
        await this.axios.put(BUSINESS_ENTITY_API, item)
        this.selectedItem = {
          id: '',
          name: '',
          business_number: '',
          bank_name: null,
          bank_number: null,
          branch_name: '',
          account_number: '',
          account_name: ''
        }
        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.update_succeed'))
        await this.loadItems()
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    onModify(item) {
      this.selectedItem = item
      this.formMode = 'update'
      this.isFormModalActive = true
    },
    onRemove(item) {
      this.$buefy.dialog.confirm({
        title: '確定刪除廠商資料?',
        message: '確定要刪除 ' + item.name + ' 廠商資料?',
        confirmText: '確定刪除',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: this.onDelete(item)
      })
    },
    async onDelete(item) {
      const api = `${BUSINESS_ENTITY_API}/${item.id}`
      this.loading = true
      try {
        await this.axios.delete(api)

        notifyService.success(this.$t('attributes.delete_succeed'))
        this.selectedItems = []
        await this.loadItems()
      } catch (e) {
        notifyService.delete_error(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
